import Cookies from 'js-cookie';


const getHostNameWithoutProtocol = (inputUrl: string): string => {
	const parsedUrl = new URL(inputUrl);
	return parsedUrl.hostname;
}

export class CookieManager {
	// http://localhost:3000/common-redirection?page=signin&token=S-658a86d2b1bd60001281019d1703770578842
	static setCookie = (key, value, domain = undefined, expires = 90, others: any = {}) => {
		Cookies.set(key, value, { expires, domain, ...others });
	}

	static getCookie = (key, params?) => {
		if (params)
			return Cookies.withAttributes(params).get(key);
		return Cookies.get(key)
	}

	static removeCookie = (key, params?) => {
		Cookies.remove(key, params)
	}

	static calculateDomain = (host: string = undefined) => {
		let hostName = host || window.location.hostname
		let subHosts = hostName.split(".")
		if (subHosts.length <= 2)
			return hostName;
		subHosts.splice(0, 1)
		return subHosts.join(".")
	}
	static setSessionToken = (val) => {
		CookieManager.removeSessionToken()
		CookieManager.setCookie("sessionToken", val, CookieManager.calculateDomain())
		// CookieManager.setCookie("session_id", "**loggedin**" + CookieManager.getCookie("session_id"))
	}
	static setTokenId = (val) => {
		CookieManager.setCookie("tokenId", val, CookieManager.calculateDomain())
	}
	static getTokenId = () => {
		return CookieManager.getCookie("tokenId")
	}
	static removeTokenId = () => {
		CookieManager.removeCookie("tokenId", { domain: CookieManager.calculateDomain() })
		CookieManager.removeCookie("tokenId")
	}

	static setSessionId = (val, readableId) => {
		this.setCookie("session_id", "**loggedin**" + val, undefined, undefined, {
			path: `/c/${readableId}/`
		})
	}
	static getSessionId = () => {
		return CookieManager.getCookie("session_id")
	}

	/** Domain-scoped auth cookies used for client-side logged-in checks (e.g. mobile popup). */
	static isAuthenticated = (): boolean => {
		return Boolean(CookieManager.getTokenId() || CookieManager.getSessionToken())
	}

	static removeSessionId = (readableId?: string) => {
		const paths = new Set<string>()
		if (typeof window !== 'undefined') {
			const match = window.location?.pathname.match(/^(\/c\/[^/]+\/)/i)
			if (match?.[0]) {
				paths.add(match[0])
			}
			paths.add(window.location.pathname)
		}
		const rid = readableId || CookieManager.getReadableId()
		if (rid) {
			paths.add(`/c/${rid}/`)
			// Use URL-derived locale (matches Next.js i18n prefix) or i18nextLng as fallback.
			// 'locale' key is unused; i18nextLng is the correct key from next-i18next / react-i18next.
			const locale = (typeof window !== 'undefined' &&
				window.location.pathname.match(/^\/([a-z]{2})(\/|$)/)?.[1]) ||
				(typeof window !== 'undefined' && localStorage.getItem('i18nextLng')) ||
				null;
			if (locale && locale !== 'en') {
				paths.add(`/${locale}/c/${rid}/`)
			}
		}
		paths.add('/')
		paths.forEach((path) => CookieManager.removeCookie('session_id', { path }))
		CookieManager.removeCookie('session_id')
	}

	static getSessionToken = () => {
		return CookieManager.getCookie("sessionToken")
	}
	static removeSessionToken = (readableId?: string) => {
		CookieManager.removeSessionId(readableId)
		CookieManager.removeCookie("sessionToken", { domain: CookieManager.calculateDomain() })
		CookieManager.removeCookie("sessionToken")
	}

	/** Clears all auth cookies on logout (tokenId, sessionToken, path-scoped session_id). */
	static clearAuthCookies = (readableId?: string) => {
		CookieManager.removeTokenId()
		CookieManager.removeSessionToken(readableId)
	}

	static setCommunityToken = (val) => {
		CookieManager.removeCommunityToken()
		CookieManager.setCookie("nextCommunityTokenV2", val, undefined, 1, {
			path: `/c/${CookieManager.getReadableId()}/`
		})
		// Derive the active Next.js locale from the URL so the path-scoped cookie matches
		// what Next.js prefixes. Fallback to i18nextLng (client i18n) when not on a
		// locale-prefixed URL (e.g. on /social-login before locale redirect).
		const activeLocale = (typeof window !== 'undefined' &&
			window.location.pathname.match(/^\/([a-z]{2})(\/|$)/)?.[1]) ||
			(typeof window !== 'undefined' && localStorage.getItem('i18nextLng')) ||
			null;
		if (activeLocale && activeLocale !== 'en') {
			CookieManager.setCookie("nextCommunityTokenV2", val, undefined, 1, {
				path: `/${activeLocale}/c/${CookieManager.getReadableId()}/`
			})
		}
		if (!process.env.NEXT_PUBLIC_NEXT_URL) {
			// ponytail: sessionStorage is tab-scoped so each tab retains its own token even
			// when another tab on the same root domain overwrites the domain-wide cookie.
			if (typeof sessionStorage !== 'undefined') sessionStorage.setItem('communityToken', val);
			CookieManager.setCookie("communityToken", val, CookieManager.calculateDomain())
			const userId = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user'))?.userId : null
			if (userId) {
				CookieManager.setCookie(`selected_community_${userId}`, localStorage.getItem('selected_community'), CookieManager.calculateDomain())
			}
		}
		if (localStorage.getItem("user")) {
			CookieManager.setCookie("session_id", "**loggedin**" + CookieManager.getCookie("session_id"))
		} else {
			CookieManager.removeSessionId()
		}
	}
	static getCommunityToken = () => {
		if (process.env.NEXT_PUBLIC_NEXT_URL) {
			return CookieManager.getCookie("nextCommunityTokenV2")
		}
		// Prefer tab-scoped sessionStorage so that cross-domain sibling tabs on the same root
		// domain (e.g. community-b.networked.co) cannot overwrite this tab's effective token
		// by writing to the shared domain-wide cookie.
		const tabToken = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('communityToken') : null;
		if (tabToken) return tabToken;
		return CookieManager.getCookie("communityToken")
	}
	static removeCommunityToken = () => {
		CookieManager.removeSessionId()
		if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem('communityToken');
		CookieManager.removeCookie("communityToken", { domain: CookieManager.calculateDomain() })
		CookieManager.removeCookie("communityToken")
		const activeLocale = (typeof window !== 'undefined' &&
			window.location.pathname.match(/^\/([a-z]{2})(\/|$)/)?.[1]) ||
			(typeof window !== 'undefined' && localStorage.getItem('i18nextLng')) ||
			null;
		// Sweep ALL previously visited community paths to prevent cookie accumulation
		const visited: string[] = (() => {
			try { return JSON.parse(localStorage.getItem('_nwk_visited_rids') || '[]') } catch { return [] }
		})()
		const currentRid = CookieManager.getReadableId()
		const allRids = new Set([...visited, currentRid].filter(Boolean))
		allRids.forEach(rid => {
			CookieManager.removeCookie("nextCommunityTokenV2", { path: `/c/${rid}/` })
			if (activeLocale && activeLocale !== 'en') {
				CookieManager.removeCookie("nextCommunityTokenV2", { path: `/${activeLocale}/c/${rid}/` })
			}
		})
		CookieManager.removeCookie("nextCommunityTokenV2")
	}

	// clear cookies related to courses
	static clearCoursesCookies = () => {
		const coursesCookies = [
			'csrftoken',
			'edxloggedin',
			'openedx-language-preference',
			'sessionid',
			'studio_session_id'
		];

		if (typeof window === "undefined") return; // SSR-safe

		const baseDomain = "networked.co";
		const hostname = window.location.hostname;

		// 🧠 Detect environment from current hostname
		let currentEnvPrefix = "";
		if (hostname.includes(".qa.")) currentEnvPrefix = "qa.";
		else if (hostname.includes(".lab.")) currentEnvPrefix = "lab.";

		// Subdomains to check
		const subDomains = ["app", "apps", "courses", "mattermost"];

		// Build all domain variants
		const allDomains = new Set<string>([
			hostname,
			baseDomain,
			`.${baseDomain}`,
			`${currentEnvPrefix}${baseDomain}`,
			`.${currentEnvPrefix}${baseDomain}`,
			...subDomains.flatMap(sub => [
				`${sub}.${currentEnvPrefix}${baseDomain}`,
				`.${sub}.${currentEnvPrefix}${baseDomain}`,
			]),
			// Nested patterns like apps.courses.qa.networked.co
			...subDomains.flatMap(parent =>
				subDomains.map(child => [
					`${child}.${parent}.${currentEnvPrefix}${baseDomain}`,
					`.${child}.${parent}.${currentEnvPrefix}${baseDomain}`,
				])
			).flat(),
		]);

		const paths = ["/", window.location.pathname];

		// ✅ Only clear cookies from the allowed list
		coursesCookies.forEach(key => {
			allDomains.forEach(domain => {
				paths.forEach(path => {
					try {
						Cookies.remove(key, { domain, path });
					} catch (err) {
						console.warn(`⚠️ Failed to remove ${key} for domain ${domain} path ${path}`, err);
					}
				});
			});
			Cookies.remove(key); // Fallback for local
		});
	};

	static setReadableId = (val) => {
		CookieManager.setCookie("readableId", val)
		if (val && typeof localStorage !== 'undefined') {
			try {
				const ids: string[] = JSON.parse(localStorage.getItem('_nwk_visited_rids') || '[]')
				if (!ids.includes(val)) {
					ids.push(val)
					localStorage.setItem('_nwk_visited_rids', JSON.stringify(ids))
				}
			} catch { /* ignore */ }
		}
	}
	static getReadableId = () => {
		return CookieManager.getCookie("readableId")
	}

	static setGroupToken = (val, groupId?, readableId?) => {
		CookieManager.setCookie("groupToken", val, CookieManager.calculateDomain())
	}
	/**
	 * While true, requests carry no space token even on a /groups page.
	 *
	 * For a community-scoped surface opened OVER a space page: the space management sheet edits a
	 * space from the community's side (`childCommunityId` writes, community-level reads), exactly
	 * as it does in Admin Pages. On `/groups/:id/...` every request would otherwise pick up that
	 * space's `groupToken` and be re-scoped to the space. Set it on open, clear it on close.
	 */
	static groupTokenSuppressed = false
	static setGroupTokenSuppressed = (suppressed: boolean) => {
		CookieManager.groupTokenSuppressed = suppressed
	}
	static getGroupToken = () => {
		if (CookieManager.groupTokenSuppressed)
			return undefined
		try {
			if (window) {
				if (!window.location.pathname.includes("group"))
					return undefined
			}
		}
		catch (err) {
			// because using window key was throwing error if used in server side
		}
		return CookieManager.getCookie("groupToken")
	}
	static removeGroupToken = (groupId?) => {
		return CookieManager.removeCookie("groupToken", { domain: CookieManager.calculateDomain() })
	}

}